'use client';

import { useSavedChats } from '@/hooks/useSavedChats';
import type { ChatListItem } from '@/types';
import { createContext, useContext, useMemo, type ReactNode } from 'react';

interface SavedChatsContextValue {
  savedChats: ChatListItem[];
  savedChatsPending: boolean;
  isChatSaved: (id: string) => boolean;
  toggleSaveChat: (id: string) => void;
}

const SavedChatsContext = createContext<SavedChatsContextValue | null>(null);

/**
 * Mounts `useSavedChats()` exactly ONCE for the whole nav-v3 tree — it runs
 * two queries and two mutation observers, so mounting it again per row (as
 * `ChatRowActionsMenu` used to) subscribed every rendered row to its own copy
 * of the saved-chats and chats queries. `PinnedChatsSection` and every
 * `ChatRowActionsMenu` read the shared value from here instead.
 */
export const SavedChatsProvider = ({ children }: { children: ReactNode }) => {
  const { savedChats, savedChatsPending, isChatSaved, toggleSaveChat } = useSavedChats();

  const value = useMemo(
    () => ({ savedChats, savedChatsPending, isChatSaved, toggleSaveChat }),
    [savedChats, savedChatsPending, isChatSaved, toggleSaveChat]
  );

  return (
    <SavedChatsContext.Provider value={value}>{children}</SavedChatsContext.Provider>
  );
};

/**
 * Reads the nav-v3-wide saved-chats state provided by `SavedChatsProvider`.
 * Throws outside it rather than silently mounting a second `useSavedChats()`
 * subscription, which is exactly the regression this hoist fixes.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useNavSavedChats = (): SavedChatsContextValue => {
  const context = useContext(SavedChatsContext);
  if (!context) {
    throw new Error('useNavSavedChats must be used within a SavedChatsProvider');
  }
  return context;
};
